<template>
  <div class="home-div">
    <!-- 艺术化背景元素 -->
    <div class="art-bg-elements">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="noise-texture"></div>
    </div>

    <SideBar />
    
    <div class="content">
      <!-- 左侧区域 - 个人信息卡片 -->
      <div class="left">
        <div class="left-div mbl art-card slide-in-left">
          <!-- 卡片装饰元素 -->
          <div class="card-decoration">
            <div class="corner-dot tl"></div>
            <div class="corner-dot tr"></div>
            <div class="corner-dot bl"></div>
            <div class="corner-dot br"></div>
            <div class="glow-border"></div>
          </div>
          
          <!-- 林地公示标题 -->
          <div class="section-header">
            <div class="title-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L3 7L12 12L21 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                <path d="M3 17L12 22L21 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3 12L12 17L21 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="section-title artistic-title">
              <span class="title-text font-color-2">林地介绍</span>
              <div class="title-underline"></div>
            </div>
          </div>
          
          <!-- 个人信息网格 -->
          <div class="info-grid">
            <div class="info-item art-item" v-for="(item, index) in infoItems" :key="index">
              <div class="info-label font-color-2">
                <span class="label-icon">›</span>
                {{ item.label }}
              </div>
              <div class="info-value font-color-4" :class="{ 'highlight': item.highlight }">
                {{ item.value }}
              </div>
            </div>
          </div>
          
          <!-- 自述部分 -->
          <div class="quote-section">
            <div class="quote-mark start">❝</div>
            <div class="quote-text font-color-4">{{ UserInfo.sign }}</div>
            <div class="quote-mark end">❞</div>
          </div>
          
          <!-- 兴趣爱好画廊 -->
          <div class="interests-gallery">
            <div class="interest-tag" v-for="(interest, idx) in interests" :key="idx">
              <span class="tag-emoji">{{ interest.emoji }}</span>
              <span class="tag-text">{{ interest.text }}</span>
            </div>
          </div>
          
          <!-- 肖像展示 - 艺术优化版 -->
          <div class="portrait-display" @click="toggleFullscreen(UserInfo.portrait)">
            <div class="portrait-frame">
              <img 
                :src="UserInfo.portrait" 
                class="portrait-img" 
                loading="lazy"
                alt="肖像"
              />
              <div class="portrait-glow"></div>
              <div class="view-fullscreen-hint">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 3L21 3M21 3L21 9M21 3L9 15M9 15L3 15M9 15L9 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
          
          <!-- 林地事记时间线 -->
          <div class="section-header">
            <div class="title-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2"/>
                <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="section-title artistic-title">
              <span class="title-text font-color-2">林地事记</span>
              <div class="title-underline"></div>
            </div>
          </div>
          
          <div class="timeline-artistic">
            <div class="timeline-line"></div>
            <div 
              class="milestone-item" 
              v-for="(item, index) in milestoneList" 
              :key="item.id"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <div class="milestone-dot" :class="`dot-${index % 3}`"></div>
              <div class="milestone-content">
                <div class="milestone-year font-color-2">{{ item.year }}</div>
                <div class="milestone-desc font-color-4" v-html="item.descInfo"></div>
              </div>
            </div>
          </div>
          
          <!-- 页脚提示 -->
          <div class="footnote font-color-2">
            <div class="footnote-icon">※</div>
            <div class="footnote-text">
              本站部分资源来自于网络，如有侵权，烦请通过上述邮箱联系，我会立即删除，谢谢！
            </div>
          </div>
        </div>
      </div>
      
      <!-- 右侧区域 - 插画与交互 -->
      <div class="right">
        
        <!-- 标题区域 -->
        <div class="title-section mbl">
          <div class="main-title artistic-title">
            <div class="title-line-1 font-color-1">Dragon Raja</div>
            <div class="title-line-2 font-color-2">Black Sheep Wall</div>
            <div class="title-divider">
              <div class="divider-line"></div>
              <div class="divider-dot"></div>
              <div class="divider-line"></div>
            </div>
          </div>
          
          <!-- 时间显示 -->
          <div class="time-display font-color-4">
            <TimesView class="times-view-art" />
          </div>
        </div>
        
        <!-- 社交链接 -->
        <div class="social-links mbl">
          <div 
            class="social-item" 
            v-for="(social, index) in socialLinks" 
            :key="index"
            @click="handleSocialClick(social.type)"
            @mouseenter="handleSocialHover(index, true)"
            @mouseleave="handleSocialHover(index, false)"
            :class="{ 
              'animate-in': socialAnimated[index],
              'social-hover': social.hovering 
            }"
          >
            <div class="social-icon">
              <component :is="social.icon" />
            </div>
            <div class="social-label">{{ social.label }}</div>
            <div class="social-hint">{{ social.hint }}</div>
          </div>
        </div>
      </div>
    </div>
    
    <BottomTip class="art-bottom-tip" />
    
    <!-- 全屏展示 -->
    <transition name="fullscreen-fade">
      <div v-if="isFullscreen" class="fullscreen-overlay art-fullscreen" @click.self="closeFullscreen">
        <div class="fullscreen-content">
          <img :src="currentFullscreenImage" class="fullscreen-image art-image" />
          <div class="fullscreen-close" @click="closeFullscreen">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
        </div>
      </div>
    </transition>

    <!-- 模态框组件 -->
    <Email :isVisible="showEmailModal" @update:visible="showEmailModal = $event" />
    <AddWx :isVisible="showWxModal" @update:visible="showWxModal = $event" :qrCodeImage="baseImgUrl + UserInfo.wxImg" />
  </div>
</template>

<script>
import SideBar from '@/components/menu/SideBar.vue';
import BottomTip from '@/components/menu/BottomTip.vue';
import TimesView from '@/components/tip/TimesView.vue';
import Email from '@/components/Email.vue';
import AddWx from '@/components/AddWx.vue';
import { getMilestoneList } from '@/api/milestone';
import { getUserInfo } from '@/api/home';
import config from '@/utils/Config';
import { Message } from 'element-ui';

// SVG图标组件
const SocialIcons = {
  EmailIcon: {
    render(h) {
      return h('svg', {
        attrs: {
          width: '24',
          height: '24',
          viewBox: '0 0 24 24',
          fill: 'none'
        }
      }, [
        h('path', {
          attrs: {
            d: 'M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z',
            stroke: 'currentColor',
            'stroke-width': '2'
          }
        }),
        h('path', {
          attrs: {
            d: 'M22 6L12 13L2 6',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round'
          }
        })
      ])
    }
  },
  WeChatIcon: {
    render(h) {
      return h('svg', {
        attrs: {
          width: '24',
          height: '24',
          viewBox: '0 0 24 24',
          fill: 'none'
        }
      }, [
        h('circle', {
          attrs: {
            cx: '16',
            cy: '18.5',
            r: '0.5',
            stroke: 'currentColor',
            'stroke-width': '2'
          }
        }),
        h('circle', {
          attrs: {
            cx: '9',
            cy: '10.5',
            r: '0.5',
            stroke: 'currentColor',
            'stroke-width': '2'
          }
        }),
        h('path', {
          attrs: {
            d: 'M16 8C19.3137 8 22 10.2386 22 13C22 14.8929 20.636 16.4877 18.757 17.3524',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round'
          }
        }),
        h('path', {
          attrs: {
            d: 'M8 16C4.68629 16 2 13.7614 2 11C2 8.23858 4.68629 6 8 6C11.3137 6 14 8.23858 14 11C14 11.739 13.8466 12.4402 13.5718 13.0763',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round'
          }
        })
      ])
    }
  }
};

export default {
  name: 'ArtisticHome',
  components: {
    SideBar,
    BottomTip,
    TimesView,
    Email,
    AddWx,
    ...SocialIcons
  },
  data() {
    return {
      UserInfo: {
        name: "",
        email: "",
        domainName: "",
        sign: "",
        hometown: "",
        anime: "",
        movies: "",
        games: "",
        descInfo: "",
        portrait: "",
        draw: "",
        wxImg: "",
        dyUrl: "",
        xhsUrl: ""
      },
      portraitHover: false,
      artworkHover: false,
      showWxModal: false,
      showEmailModal: false,
      isFullscreen: false,
      currentFullscreenImage: '',
      milestoneList: [],
      baseImgUrl: config.BASE_FILE_URL,
      socialLinks: [
        { 
          type: 'email', 
          label: '邮箱联系', 
          hint: 'Send Email', 
          icon: 'EmailIcon',
          hovering: false
        },
        { 
          type: 'wechat', 
          label: '添加微信', 
          hint: 'Add WeChat', 
          icon: 'WeChatIcon',
          hovering: false
        }
      ],
      socialAnimated: []
    };
  },
  computed: {
    infoItems() {
      return [
        { label: '网名', value: this.UserInfo.name, highlight: true },
        { label: '家乡', value: this.UserInfo.hometown },
        { label: '邮箱', value: this.UserInfo.email },
        { label: '域名', value: this.UserInfo.domainName }
      ];
    },
    interests() {
      return [
        { emoji: '🎬', text: this.UserInfo.anime },
        { emoji: '🎥', text: this.UserInfo.movies },
        { emoji: '🎮', text: this.UserInfo.games }
      ];
    }
  },
  async mounted() {
    await this.loadPriorityData();
    this.initAnimations();
    this.initMouseEffects();
    
    // ESC 关闭全屏
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeFullscreen();
    });
  },
  methods: {
    async loadPriorityData() {
      try {
        await Promise.all([
          this.getMilestoneList(),
          this.getUserInfo(),
        ]);
      } catch (error) {
        Message.error('加载数据失败: ' + error.message);
      }
    },
    async getMilestoneList() {
      const resp = await getMilestoneList({});
      if (resp.data) {
        this.milestoneList = resp.data;
      }
    },
    async getUserInfo() {
      const resp = await getUserInfo({ id: config.BASE_USER_ID });
      if (resp.data) {
        this.UserInfo = resp.data;
        this.UserInfo.portrait = config.BASE_FILE_URL + resp.data.portrait;
        this.UserInfo.draw = config.BASE_FILE_URL + resp.data.draw;
      }
    },
    initAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = Array.from(entry.target.parentElement.children).indexOf(entry.target);
            this.$set(this.socialAnimated, index, true);
            if (entry.target.classList.contains('art-item')) {
              entry.target.classList.add('animate-in');
            } else if (entry.target.classList.contains('milestone-item')) {
              entry.target.classList.add('animate-in');
            }
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);
      document.querySelectorAll('.art-item, .milestone-item, .social-item').forEach(el => {
        observer.observe(el);
      });
    },
    initMouseEffects() {
      document.addEventListener('mousemove', (e) => {
        const mouseX = e.clientX / window.innerWidth;
        const mouseY = e.clientY / window.innerHeight;
        document.querySelectorAll('.floating-orb').forEach((orb, index) => {
          const speed = 0.5 + index * 0.2;
          const x = (mouseX - 0.5) * 40 * speed;
          const y = (mouseY - 0.5) * 40 * speed;
          orb.style.transform = `translate(${x}px, ${y}px)`;
        });
      });
    },
    handleSocialHover(index, isHovering) {
      this.$set(this.socialLinks[index], 'hovering', isHovering);
    },
    handleSocialClick(type) {
      switch (type) {
        case 'email':
          this.showEmailModal = true;
          break;
        case 'wechat':
          this.showWxModal = true;
          break;
        case 'tiktok':
          window.open(this.UserInfo.dyUrl || '#', '_blank');
          break;
        case 'xiaohongshu':
          window.open(this.UserInfo.xhsUrl || '#', '_blank');
          break;
      }
    },
    
    // 全屏图片展示
    toggleFullscreen(imgUrl) {
      this.currentFullscreenImage = imgUrl;
      this.isFullscreen = true;
      document.body.style.overflow = 'hidden';
    },
    closeFullscreen() {
      this.isFullscreen = false;
      document.body.style.overflow = '';
    }
  }
};
</script>

<style scoped>
/* ===== 基础样式 ===== */
.home-div {
  width: 100%;
  min-height: 100vh;
  padding-top: 80px;
  position: relative;
  overflow: hidden;
}

/* ===== 艺术背景元素 ===== */
.art-bg-elements {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.noise-texture {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
  opacity: 0.03;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  animation: float 20s infinite ease-in-out;
}

.orb-1 {
  width: 300px;
  height: 300px;
  top: 10%;
  left: 10%;
  background: radial-gradient(circle, rgba(120, 119, 198, 0.15) 0%, transparent 70%);
  animation-delay: 0s;
}

.orb-2 {
  width: 200px;
  height: 200px;
  top: 60%;
  right: 15%;
  background: radial-gradient(circle, rgba(255, 119, 198, 0.1) 0%, transparent 70%);
  animation-delay: -5s;
}

.orb-3 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  left: 20%;
  background: radial-gradient(circle, rgba(100, 200, 255, 0.1) 0%, transparent 70%);
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) translateX(0px); }
  25% { transform: translateY(-20px) translateX(10px); }
  50% { transform: translateY(10px) translateX(-15px); }
  75% { transform: translateY(-15px) translateX(5px); }
}

/* ===== 内容布局 ===== */
.content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  gap: 40px;
  position: relative;
  z-index: 1;
}

.left, .right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* ===== 艺术卡片样式 ===== */
.art-card {
  position: relative;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 10px !important;
  overflow: hidden;
}

.card-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.corner-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}

.corner-dot.tl { top: 15px; left: 15px; }
.corner-dot.tr { top: 15px; right: 15px; }
.corner-dot.bl { bottom: 15px; left: 15px; }
.corner-dot.br { bottom: 15px; right: 15px; }

.glow-border {
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border-radius: 10px;
  background: linear-gradient(45deg, 
    transparent, 
    rgba(255, 255, 255, 0.1), 
    transparent
  );
  opacity: 0;
  transition: opacity 0.6s ease;
}

.art-card:hover .glow-border {
  opacity: 1;
}

/* ===== 标题样式 ===== */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
}

.title-icon {
  color: var(--font-color-2);
  opacity: 0.8;
}

.artistic-title {
  position: relative;
  display: inline-block;
}

.title-text {
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.title-underline {
  height: 2px;
  width: 60%;
  background: linear-gradient(90deg, 
    transparent, 
    var(--font-color-2), 
    transparent
  );
  margin-top: 4px;
}

/* ===== 个人信息网格 ===== */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.info-item {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.info-item.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.info-label {
  font-size: 0.9rem;
  opacity: 0.7;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-icon {
  color: var(--font-color-1);
}

.info-value {
  font-size: 1.1rem;
  font-weight: 500;
}

.info-value.highlight {
  color: var(--font-color-1);
  font-weight: 600;
}

/* ===== 引用部分 ===== */
.quote-section {
  margin: 40px 0;
  padding: 20px;
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border-left: 3px solid rgba(255, 255, 255, 0.1);
}

.quote-mark {
  position: absolute;
  font-size: 2rem;
  opacity: 0.2;
}

.quote-mark.start { top: 10px; left: 15px; }
.quote-mark.end { bottom: 10px; right: 15px; }

.quote-text {
  font-size: 1.1rem;
  line-height: 1.8;
  text-align: center;
  font-style: italic;
  padding: 0 20px;
}

/* ===== 兴趣爱好画廊 ===== */
.interests-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 30px 0;
}

.interest-tag {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  color: var(--font-color-2);
}

.interest-tag:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.tag-emoji {
  font-size: 1.2rem;
}

.tag-text {
  font-size: 0.95rem;
}

/* ===== 优化后的艺术肖像展示 ===== */
.portrait-display {
  margin: 50px 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.portrait-frame {
  position: relative;
  /* width: 280px; */
  height: 380px;
  margin: 0 auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.portrait-frame:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
  border-color: rgba(255, 255, 255, 0.25);
}

.portrait-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: all 0.5s ease;
}

.portrait-frame:hover .portrait-img {
  transform: scale(1.05);
}

.portrait-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    transparent 60%,
    rgba(0, 0, 0, 0.35) 150%
  );
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 1;
}

.portrait-frame:hover .portrait-glow {
  opacity: 1;
}

.view-fullscreen-hint {
  position: absolute;
  bottom: 18px;
  right: 18px;
  width: 38px;
  height: 38px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  opacity: 0;
  transform: scale(0.8) translateY(10px);
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  z-index: 2;
}

.portrait-frame:hover .view-fullscreen-hint {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* ===== 时间线样式 ===== */
.timeline-artistic {
  position: relative;
  margin: 40px 0 20px;
  padding-left: 30px;
}

.timeline-line {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
}

.milestone-item {
  position: relative;
  margin-bottom: 30px;
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.milestone-item.animate-in {
  opacity: 1;
  transform: translateX(0);
}

.milestone-dot {
  position: absolute;
  left: -36px;
  top: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--font-color-2);
  transform: scale(1);
  transition: transform 0.3s ease;
}

.milestone-dot.dot-0 { background: #FF6B8B; }
.milestone-dot.dot-1 { background: #4ECDC4; }
.milestone-dot.dot-2 { background: #FFD166; }

.milestone-item:hover .milestone-dot {
  transform: scale(1.3);
}

.milestone-year {
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 6px;
}

.milestone-desc {
  font-size: 1rem;
  line-height: 1.6;
  padding-right: 20px;
}

/* ===== 页脚提示 ===== */
.footnote {
  margin-top: 40px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  font-size: 0.9rem;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.footnote-icon {
  font-size: 1.2rem;
  opacity: 0.5;
}

.footnote-text {
  flex: 1;
  line-height: 1.6;
}

/* ===== 右侧区域样式 ===== */
.title-section {
  text-align: center;
  padding: 30px !important;
  border-radius: 10px;
}

.main-title {
  margin-bottom: 25px;
}

.title-line-1 {
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 8px;
  font-family: 'Playfair Display', serif;
}

.title-line-2 {
  font-size: 1.2rem;
  opacity: 0.8;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 20px;
}

.title-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 20px 0;
}

.divider-line {
  width: 80px;
  height: 1px;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.3), 
    transparent
  );
}

.divider-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
}

.time-display {
  margin-top: 20px;
}

.times-view-art {
  font-size: 1.1rem;
  letter-spacing: 1px;
}

/* ===== 社交链接 ===== */
.social-links {
  padding: 25px !important;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-radius: 10px;
  gap: 15px;
}

.social-item {
  padding: 15px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  opacity: 0;
  transform: translateY(20px);
  transition: 
    opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.6s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.3s ease,
    border-color 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
}

.social-item.animate-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

.social-item.social-hover {
  transform: translateY(-5px) translateZ(0) !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  box-shadow: 
    0 10px 20px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

.social-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--font-color-2);
  transition: color 0.3s ease;
}

.social-item.social-hover .social-icon {
  color: var(--font-color-1);
  transform: scale(1.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.social-label {
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  transition: all 0.3s ease;
}

.social-item.social-hover .social-label {
  color: var(--font-color-1);
}

.social-hint {
  font-size: 0.8rem;
  opacity: 0.6;
  text-align: center;
  transition: all 0.3s ease;
}

.social-item.social-hover .social-hint {
  opacity: 0.8;
  transform: translateY(2px);
}

.social-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 6px;
  pointer-events: none;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.05)
  );
}

.social-item.social-hover::after {
  opacity: 1;
}

/* ===== 全屏展示 ===== */
.fullscreen-fade-enter-active,
.fullscreen-fade-leave-active {
  transition: opacity 0.5s ease;
}

.fullscreen-fade-enter,
.fullscreen-fade-leave-to {
  opacity: 0;
}

.art-fullscreen {
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.fullscreen-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.art-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.fullscreen-close {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #fff;
}

.fullscreen-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* ===== 返回顶部按钮 ===== */
.back-top-btn {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--font-color-1);
  transition: all 0.3s ease;
}

.back-top-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-5px);
}

/* ===== 底部提示 ===== */
.art-bottom-tip {
  position: relative;
  z-index: 1;
  margin-top: 60px;
}

/* ===== 动画入口 ===== */
.slide-in-left, .slide-in-right {
  opacity: 0;
}

.slide-in-left {
  animation: slideLeftIn 1s cubic-bezier(0.4, 0, 0.2, 1) 0.3s forwards;
}

.slide-in-right {
  animation: slideRightIn 1s cubic-bezier(0.4, 0, 0.2, 1) 0.5s forwards;
}

@keyframes slideLeftIn {
  to {
    opacity: 1;
    transform: translateX(0);
  }
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
}

@keyframes slideRightIn {
  to {
    opacity: 1;
    transform: translateX(0);
  }
  from {
    opacity: 0;
    transform: translateX(30px);
  }
}

/* ===== 响应式设计 ===== */
@media (max-width: 1200px) {
  .content {
    flex-direction: column;
    padding: 0 30px;
  }
  .left, .right {
    width: 100%;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .content {
    padding: 0 20px;
  }
  .art-card, .title-section, .social-links {
    padding: 20px !important;
  }
  .title-line-1 {
    font-size: 2rem;
  }
  .social-links {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .home-div {
    padding-top: 60px;
  }
  .content {
    padding: 0 15px;
    gap: 20px;
  }
}
</style>